import { TextBlock } from '@babylonjs/gui/2D/controls/textBlock';
import { Rectangle } from '@babylonjs/gui/2D/controls/rectangle';
import { Control } from '@babylonjs/gui/2D/controls/control';
//! Depending on BabylonJS version, we could need that
// import '@babylonjs/core/Materials/Textures/dynamicTexture';
import { AdvancedDynamicTexture } from '@babylonjs/gui/2D/advancedDynamicTexture';

import { SystemCamera } from '../System/systemCamera';
import { CardOptions, ElementCard } from './elementCard';
import { CSS_TO_FURY } from '~src/Tools/toolStyleTranslation';

export type Alignement = 'left' | 'center' | 'right'

export interface BorderOptions {
    border?: number,
    borderColor?: string | number[],
    padding?: number,
}

export interface OutlineOptions {
    outlineWidth?: number,
    outlineColor?: string | number[],
}

export interface StyleOptions {
    fontStyle?: 'italic' | 'normal',
    fontWeight?: '800' | '400',
    textAlign?: Alignement,
    textDecoration?: 'underline' | 'none',
}

export interface TextOptions extends
BorderOptions,
OutlineOptions,
StyleOptions,
CardOptions
{
    text?: string,
    fontFamily?: string,
    fontSize?: number,
    lineHeight?: number,
    color?: string | number[],
    backgroundColor?: string | number[],
}

export const HEADING_TAG = 'h1';
export const TEXT_TAG = 'p';
export const BUTTON_TAG = 'a';

export const TEXT_BUTTON_HEADING_TAGS = [HEADING_TAG, TEXT_TAG, BUTTON_TAG];

const getLinkTargetAttrValue = (href: string): string => {
    if (href.includes('http')) {
        return '_blank';
    }
    return null;
};

export class ElementText extends ElementCard {
    constructor(system: SystemCamera, html: HTMLElement) {
        super(system, html);
        this.buildText();
        this.checkLink();
    }

    private advancedTexture: AdvancedDynamicTexture;

    private text: TextBlock;

    private container: Rectangle;

    private buildText() {
        this.container = new Rectangle();
        this.container.thickness = 0;
        this.container.isHitTestVisible = false;

        this.text = new TextBlock();
        this.text.topInPixels = 1;
        this.text.textWrapping = true;
        this.text.isHitTestVisible = false;

        // If adapt is needed
        // this.container.adaptHeightToChildren = true;
        // this.container.adaptWidthToChildren = true;
        // this.text.resizeToFit = true; // Or adaptToChildren doesn't work

        this.container.addControl(this.text);
    }

    private setAdvancedTexture() {
        if (this.advancedTexture) {
            this.advancedTexture.removeControl(this.container);
            this.advancedTexture.dispose();
        }
        const size = this.getSize();
        const width = size.width * this.textureQuality;
        const height = size.height * this.textureQuality;

        this.advancedTexture = AdvancedDynamicTexture.CreateForMeshTexture(
            this.mesh,
            width,
            height
        );
        //* Test so that border edge are perfect but on big planes it soesn't work
        // const scale = -0.01;
        // this.advancedTexture.uScale += scale;
        // this.advancedTexture.vScale += scale;
        // this.advancedTexture.uOffset -= scale / 2;
        // this.advancedTexture.vOffset -= scale / 2;

        this.advancedTexture.addControl(this.container);
        this.setTextureSampler(this.advancedTexture);
    }

    public checkGeometry() {
        this.checkPosition();
        const changed = this.checkScalingCard();
        if (changed || !this.advancedTexture) {
            this.setAdvancedTexture();
            this.checkOptions();
        } else {
            this.checkOptions();
        }
        //! Keep alphaIndex after so that the last mesh has the right index
        this.checkAlphaIndex();
        this.checkLink();
    }

    public checkLink() {
        const href = this.html.getAttribute('href');
        if (href) {
            const target = getLinkTargetAttrValue(href);
            if (target) this.html.setAttribute('target', target);
            else this.html.removeAttribute('target');
        }
    }

    public getOptions(): TextOptions {
        const style = window.getComputedStyle(this.html, null);
        const textOptions: TextOptions = {
            text: this.html.innerText
        };
        CSS_TO_FURY.forEach((cf) => {
            if (cf.type === 'number') textOptions[cf.fury] = parseFloat(style.getPropertyValue(cf.css)) || 0;
            if (cf.type === 'string') textOptions[cf.fury] = style.getPropertyValue(cf.css) || '';
            if (cf.type === 'color') textOptions[cf.fury] = style.getPropertyValue(cf.css) || '';
        });
        return textOptions;
    }

    private checkOptions() {
        const textOptions = this.getOptions();
        this.text.text = textOptions.text;
        if (textOptions.text) {
            this.mesh.isVisible = true;
            this.setColor(textOptions);
            this.setFont(textOptions);
            this.setPadding(textOptions);
            this.setBorder(textOptions);
            this.setOutline(textOptions);
        } else {
            this.mesh.isVisible = false;
        }
    }

    private setColor(textOptions: TextOptions) {
        this.text.color = textOptions.color;
        this.container.background = textOptions.backgroundColor;
        // For test purpose
        // this.container.background = 'rgb(0,255,0, 1)';
        // this.advancedTexture.background = 'rgb(255,0,0, 1)';
    }

    private setFont(textOptions: TextOptions) {
        this.text.fontWeight = textOptions.fontWeight;
        this.text.fontFamily = textOptions.fontFamily;
        this.text.fontStyle = textOptions.fontStyle;
        this.text.underline = (textOptions.textDecoration === 'underline');
        // Possibility to add stoke through
        // https://forum.babylonjs.com/t/adding-underline-line-through-to-textblock/13634/7

        let babylonTextAlign = Control.HORIZONTAL_ALIGNMENT_CENTER;
        if (textOptions.textAlign === 'left') babylonTextAlign = Control.HORIZONTAL_ALIGNMENT_LEFT;
        if (textOptions.textAlign === 'right') babylonTextAlign = Control.HORIZONTAL_ALIGNMENT_RIGHT;
        this.text.textHorizontalAlignment = babylonTextAlign;

        if (textOptions.lineHeight && textOptions.fontSize) {
            // 1.1 to avoid text being outside of plane
            this.text.lineSpacing = textOptions.lineHeight - textOptions.fontSize;
        } else {
            delete this.text.lineSpacing;
        }
    }

    private getContainerHeight(): number {
        const thicknessHeight = 2 * this.container.thickness;
        const height = this.container.heightInPixels / this.lineHeightRatio || 0;
        return Math.max(height - thicknessHeight, 0);
    }

    public updateFontHeight() {
        if (!this.text.text || this.isCheckingFont) return;
        const style = window.getComputedStyle(this.html, null);
        const { lineHeight, fontSize } = style;
        this.lineHeightRatio = (lineHeight !== 'normal') ? parseInt(lineHeight, 10) / parseInt(fontSize, 10) : 1;

        const containerHeight = this.getContainerHeight();
        const hasContainerChange = containerHeight !== this.currentContainerHeight;
        const textHeight = this.text.computeExpectedHeight();
        const hasTextChange = textHeight !== this.currentTextHeight;
        if (hasContainerChange || hasTextChange) {
            this.isCheckingFont = true;
            // Up the precision to make sure le loop start again
            this.precision += this.minPrecision * 100;
            const { onAfterRenderObservable } = this.system.scene;
            onAfterRenderObservable.add(this.loopFontHeight, 1, false, this);
        }
    }

    private previusSign = -1;

    private lineHeightRatio = 1;

    private precision = 9;

    private minPrecision = 1;

    //* null and not 0 to force first update
    private currentContainerHeight = null;

    private currentTextHeight = null;

    public isCheckingFont = false;

    // Playground example: https://playground.babylonjs.com/#ZI9AK7#3230
    private loopFontHeight() {
        const containerHeight = this.getContainerHeight();
        this.currentContainerHeight = containerHeight;
        if (!containerHeight) return;

        const textHeight = this.text.computeExpectedHeight();
        this.currentTextHeight = textHeight;

        const diff = textHeight - containerHeight;
        const newSign = Math.sign(diff);

        // newSign > 0 Means text is bigger than container
        // this.precision > this.minPrecision Means text is not yet at the right size
        // diff === 0 in case we reach the prefect size and signs are both 0
        const isPreciseEnough = this.precision < this.minPrecision;
        const textIsSmallerThanContainer = newSign < 0;
        if ((isPreciseEnough && textIsSmallerThanContainer) || diff === 0) {
            // this.container.alpha = 1; // Change alpha so that we don't see text size transition
            this.isCheckingFont = false;
            const obs = this.system.scene.onAfterRenderObservable;
            obs.removeCallback(this.loopFontHeight, this);
        } else {
            // this.container.alpha = 0; // Change alpha so that we don't see text size transition
            // Update precision factor
            if (newSign !== this.previusSign) this.precision /= 3;
            this.previusSign = newSign;
            // Update font and height
            const change = newSign * this.precision;
            this.text.fontSizeInPixels -= change;
            this.text.fontSizeInPixels = Math.max(this.text.fontSizeInPixels, 1);
        }
    }

    private setBorder(borderOptions: BorderOptions) {
        const { border, borderColor } = borderOptions;
        const thicknessAdjusted = border * 2;
        this.container.thickness = thicknessAdjusted;
        this.container.color = borderColor;
        const extrudeShape = this.getExtrudeShapeOptions();
        const { radius } = extrudeShape;
        this.container.cornerRadius = this.textureQuality * radius - (thicknessAdjusted / 2);
    }

    private setOutline(textOptions: OutlineOptions) {
        this.text.outlineWidth = textOptions.outlineWidth * 10;
        this.text.outlineColor = textOptions.outlineColor;
    }

    private setPadding(textOptions: BorderOptions) {
        const paddingWithRatio = textOptions.padding * 2;
        this.text.paddingTop = paddingWithRatio;
        this.text.paddingBottom = paddingWithRatio;
        this.text.paddingLeft = paddingWithRatio;
        this.text.paddingRight = paddingWithRatio;
    }
}
